import React, { PropsWithChildren, useMemo, useState, useEffect } from "react";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { CUSTOMER_LIST_PREFERENCES_KEY } from "@jugl-web/utils/storage";
import {
  CustomersContext,
  CustomersContextValue,
  SortStateType,
} from "./CustomersContext";
import { useCustomerFields } from "../hooks/useCustomerFields";

interface SessionStoragePreferences {
  searchQuery: string;
  searchQueryFilters: string[];
  sortState: SortStateType;
}

export const CustomersProvider: React.FC<
  PropsWithChildren & { entityId: string; persistentSearchQuery?: boolean }
> = ({ children, entityId, persistentSearchQuery }) => {
  const [customerListPreferences, setCustomerListPreferences] = useLocalStorage<
    Record<string, SessionStoragePreferences>
  >(`${CUSTOMER_LIST_PREFERENCES_KEY}`, {});
  const { formFields, getFieldDefinition } = useCustomerFields({ entityId });

  const [queryFilters, setQueryFilters] = useState<string[]>(
    customerListPreferences?.[entityId]?.searchQueryFilters || []
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    persistentSearchQuery
      ? customerListPreferences?.[entityId]?.searchQuery || ""
      : ""
  );
  const [sortState, setSortState] = useState<SortStateType>(
    customerListPreferences[entityId]?.sortState || {
      sortBy: undefined,
      sortOrder: "asc",
    }
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCustomerListPreferences((prev) => {
        const currentPreferences = prev[entityId] || {
          searchQuery: "",
          searchQueryFilters: [],
          sortState: {
            sortBy: undefined,
            sortOrder: "asc",
          },
        };
        return {
          ...prev,
          [entityId]: {
            ...currentPreferences,
            searchQuery: persistentSearchQuery
              ? searchQuery
              : currentPreferences.searchQuery,
            searchQueryFilters: queryFilters,
            sortState,
          },
        };
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    queryFilters,
    sortState,
    entityId,
    setCustomerListPreferences,
    persistentSearchQuery,
  ]);

  const transformedSearchBy = useMemo(() => {
    const firstNameField = formFields.find(
      (field) => field.name === "first_name" && field.section === "personal"
    );

    if (
      !formFields ||
      !queryFilters ||
      queryFilters.length === 0 ||
      !firstNameField
    ) {
      return undefined;
    }

    return [
      {
        id: firstNameField.id,
        values: null,
      },
      ...queryFilters.map((fieldId) => {
        const fieldDef = getFieldDefinition(fieldId);
        if (!fieldDef || fieldDef.name === "first_name") {
          return undefined;
        }
        if (fieldDef.type === "dropdown") {
          return {
            id: fieldDef.id,
            values: fieldDef.values,
          };
        }
        return {
          id: fieldDef.id,
          values: null,
        };
      }),
    ].filter(
      (
        item
      ): item is {
        id: string;
        values:
          | { id: string; value: string; color: string; order: number }[]
          | null;
      } => item !== undefined
    );
  }, [formFields, getFieldDefinition, queryFilters]);

  const value: CustomersContextValue = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      queryFilters,
      setQueryFilters,
      sortState,
      setSortState,
      transformedSearchBy,
    }),
    [queryFilters, searchQuery, sortState, transformedSearchBy]
  );

  return (
    <CustomersContext.Provider value={value}>
      {children}
    </CustomersContext.Provider>
  );
};
